/**
 * `/internal/notifications/announce` wire contract — the operator-authored
 * `kind:"system"` broadcast (#1396 follow-up).
 *
 * `system` has been in the frozen `NotificationKind` union since #1396 with
 * no producer at all; this is it. The immediate driver is announcing a newly
 * listed model to tenants, but nothing here knows what a model is: the
 * operator writes the copy, so the same surface carries maintenance windows
 * and policy changes without a second contract.
 *
 * snake_case, like every other `/internal/*` ops shape — ops-api relays these
 * bodies verbatim and ops-console reads them directly.
 *
 * THE SLUG IS THE IDEMPOTENCY KEY, and it is the operator's to choose. It
 * lands inside the fingerprint (`v1:system:announce:<slug>`), which clients
 * compare by string equality and never parse (#1396). Two consequences the
 * console has to state out loud, because neither is recoverable by editing:
 *   - Re-announcing an EXISTING slug does not create a second event. It
 *     rewrites the title/description in place and advances `last_seen_at`,
 *     so every client past its cursor sees the corrected copy — which is
 *     what makes fixing a typo work, and what makes a recycled slug
 *     silently overwrite an unrelated announcement.
 *   - A NEW slug is a new event for every targeted org, forever. There is no
 *     unsend.
 *
 * AUDIENCE IS EVERY TENANT ORG BY DEFAULT, and the only way to narrow it is
 * naming the recipients one at a time (#1965, #1968). The surface carried a
 * `primary_brand` filter until the brands collapsed onto nxtpath; what it
 * actually selected was `orgs.primary_brand`, frozen at signup from the console
 * origin, so it silently cut an announcement down to the orgs that happened to
 * register on that brand's domain — 9 of 592 on dev when it took #1945 down.
 * That filter is refused rather than ignored, and nothing brings a CATEGORY
 * back: the only narrowing accepted is an explicit list of individually named
 * recipients, every one of which has to resolve or the whole send is refused.
 *
 * ── THE WHITELIST (`test_recipients`), AND WHY IT IS SHAPED LIKE THIS ───────
 * A whitelist send exists so an operator can look at the real thing in a real
 * client before the fleet does. Four properties of the shape are load-bearing:
 *
 *   - AN ENTRY IS AN EMAIL OR AN ORG ID, and email is the point (#1968).
 *     Operators know `dean@example.com`; nobody knows `org_34a69360d35fd77d`
 *     without first looking it up on the customer page. Demanding the id put a
 *     lookup between an operator and the safety feature meant to be used every
 *     time, and friction on a safety feature is how it stops being used. What
 *     an entry IDENTIFIES and what the send WRITES are independent: an email
 *     resolves to that person's org and the row written is still the org-wide
 *     `account_id = NULL` broadcast, byte for byte the shape a fleet send
 *     writes. Rehearsing down a path production never takes would be worth
 *     nothing, and it is not the price of accepting an email.
 *
 *   - IT USES THE REAL FINGERPRINT NAMESPACE. There is no `announce-test:`
 *     space, deliberately: the follow-up fleet send of the same slug then
 *     UPGRADES the whitelisted org's row in place (`ON CONFLICT`), so the
 *     person who proof-read the draft ends up holding exactly one notification
 *     carrying the final copy. A separate namespace would leave them holding
 *     two unrelated messages, the first of which is an unproofed draft.
 *   - ABSENT MEANS THE FLEET; EMPTY AND NULL ARE REFUSED. Omitting the field is
 *     the pre-#1965 behaviour byte for byte. `[]` is NOT read as "no filter": a
 *     caller that sent an empty list believes it scoped the send, and quietly
 *     broadcasting to 592 orgs on that belief is #1945 pointed the other way —
 *     the same reason `primary_brand` is refused rather than ignored. `null`
 *     goes with `[]` rather than with absent: `JSON.stringify` drops an
 *     undefined property instead of emitting null, so a null on the wire is a
 *     value somebody chose, and `list.length ? list : null` is the same
 *     collapse of "narrowed" into "fleet" moved one layer up.
 *   - AN UNRESOLVED ENTRY FAILS THE WHOLE SEND. Dropping the entries that
 *     matched nothing would send to 7 orgs while the operator counted 10,
 *     which is the #1945 failure in miniature. There is no unsend, so the
 *     refusal happens before anything is written and the response names every
 *     entry it could not resolve, verbatim, so a typo is findable. An email
 *     belonging to somebody in two orgs is refused the same way rather than
 *     picked between: guessing which org to announce to is exactly the class
 *     of silent audience decision this surface exists to not make.
 *
 * `mode` in the response is the anti-#1945 echo. A caller that asked for a
 * whitelist and is told `fleet` learned that its narrowing did not happen —
 * which is what a rolled-back platform would answer, since a build predating
 * #1965 ignores the field entirely and broadcasts. The console treats that
 * disagreement as a failure rather than a send.
 */

import type { NotificationSeverity } from './public-notifications.ts';

/** Fingerprint namespace for operator broadcasts. `v1:` prefix per #1396. */
export const NOTIFICATION_ANNOUNCE_FINGERPRINT_NAMESPACE = 'v1:system:announce:';

/**
 * Lowercase, no colons. The colon is the fingerprint's own separator, so
 * admitting one would let an operator forge a neighbouring namespace.
 */
export const NOTIFICATION_ANNOUNCE_SLUG_PATTERN = /^[a-z0-9][a-z0-9._-]{2,63}$/;

export const NOTIFICATION_ANNOUNCE_TITLE_MAX = 100;
export const NOTIFICATION_ANNOUNCE_DESCRIPTION_MAX = 2_000;

/**
 * Ceiling on a whitelist send's recipient list (#1965).
 *
 * Sized for the job, not for the database: a whitelist send is for eyeballs on
 * a draft — the operator's own org, a colleague, a friendly customer. A list
 * long enough to need paging is not a proof-read, it is an audience, and an
 * audience is what the fleet send is for. Keeping it small also bounds the
 * refusal message, which names every entry it could not resolve.
 */
export const NOTIFICATION_ANNOUNCE_TEST_RECIPIENTS_MAX = 20;

/**
 * Which audience a send actually reached, echoed back by the route.
 *
 * `fleet` is every non-system org; `whitelist` is exactly the orgs the request
 * named. It exists so the caller can detect the one failure that is otherwise
 * silent — see the header note on `mode`.
 */
export type OpsAnnounceAudienceMode = 'fleet' | 'whitelist';

/**
 * The `ops_audit` coordinates of a send (#1946).
 *
 * SHARED BECAUSE BOTH SIDES OF A RECORD DEPEND ON THEM. `ops_audit` is the
 * only place a past announcement survives — the notifications rows hold just
 * the latest copy — so platform-api writes these two values and the console's
 * history panel filters on them. Hard-coded at each end, changing the writer
 * would leave the reader querying a string nothing emits, and the panel would
 * render an empty list rather than an error.
 */
export const NOTIFICATION_ANNOUNCE_AUDIT_ACTION = 'notification.announced';
export const NOTIFICATION_ANNOUNCE_AUDIT_TARGET_TYPE = 'notification_broadcast';

export interface OpsAnnounceNotificationRequest {
  /** Idempotency key; see the header note. Matches the slug pattern above. */
  readonly slug: string;
  readonly title: string;
  readonly description: string;
  /** Defaults to `info` — an announcement is not an incident. */
  readonly severity?: NotificationSeverity;
  /**
   * Whitelist send (#1965, #1968): write ONLY the orgs these entries resolve
   * to. Each entry is an EMAIL (resolved to that person's org) or an org id.
   * OMIT for the fleet — an empty array and an explicit null are both refused,
   * and so is any entry that resolves to nothing, to a system org, or to more
   * than one org. See the header note for why none of them degrades into a
   * broadcast.
   */
  readonly test_recipients?: readonly string[];
}

export interface OpsAnnounceNotificationResponse {
  /** Echoed so the console can show what was actually written. */
  readonly fingerprint: string;
  /**
   * Which audience this send actually reached — `whitelist` only when the
   * request named orgs AND the route honoured the list. A caller that asked to
   * narrow and reads `fleet` here has been told its narrowing did not happen.
   *
   * REQUIRED IS THE WRITER'S OBLIGATION, NOT A PROMISE TO THE READER. The whole
   * point of the field is to catch a platform that predates #1965, and such a
   * platform sends no `mode` at all — so at runtime a reader can and will see
   * `undefined` here, whatever this declaration says. A client must therefore
   * narrow the value it received rather than trust this type; see
   * `apps/ops-console/src/api/notificationAnnouncements.ts`, which does exactly
   * that and hands its callers an explicitly optional mode.
   */
  readonly mode: OpsAnnounceAudienceMode;
  /** Orgs written: every non-system org, or exactly the whitelist. */
  readonly orgs_targeted: number;
  /** Orgs that had no row for this slug before: the genuinely new reach. */
  readonly created: number;
  /**
   * Orgs that already held this slug and were rewritten in place. Non-zero on
   * a first announcement means the slug was recycled.
   */
  readonly resurfaced: number;
}
